<script setup lang="ts">
import { onBeforeUnmount, watch } from 'vue';
import { X } from '@lucide/vue';
import { cn } from '@/shared/utils/cn';

type Size = 'sm' | 'md' | 'lg' | 'xl';

const props = withDefaults(
  defineProps<{
    open: boolean;
    title?: string | null | undefined;
    description?: string | null | undefined;
    size?: Size | undefined;
    persistent?: boolean | undefined;
    hideClose?: boolean | undefined;
    zIndexClass?: string | undefined;
  }>(),
  { size: 'md', persistent: false, hideClose: false, title: null, description: null, zIndexClass: 'z-50' },
);

const emit = defineEmits<{ (e: 'update:open', v: boolean): void; (e: 'close'): void }>();

function requestClose(): void {
  if (props.persistent) return;
  emit('update:open', false);
  emit('close');
}

function onKey(ev: KeyboardEvent): void {
  if (!props.open) return;
  if (ev.key === 'Escape') requestClose();
}

watch(
  () => props.open,
  (v) => {
    if (typeof document === 'undefined') return;
    document.documentElement.style.overflow = v ? 'hidden' : '';
  },
);

if (typeof window !== 'undefined') {
  window.addEventListener('keydown', onKey);
}

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') window.removeEventListener('keydown', onKey);
  if (typeof document !== 'undefined') document.documentElement.style.overflow = '';
});
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-150"
      leave-active-class="transition-opacity duration-150"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div v-if="open" :class="cn('fixed inset-0 flex items-center justify-center p-4', zIndexClass)">
        <button
          type="button"
          class="absolute inset-0 bg-black/40 backdrop-blur-md"
          aria-label="close"
          @click="requestClose"
        />
        <Transition
          enter-active-class="transition duration-200"
          leave-active-class="transition duration-150"
          enter-from-class="opacity-0 translate-y-2 scale-[0.98]"
          leave-to-class="opacity-0 translate-y-2 scale-[0.98]"
        >
          <div
            v-if="open"
            role="dialog"
            aria-modal="true"
            :class="cn(
              'relative z-10 flex max-h-[90dvh] w-full flex-col overflow-hidden rounded-2xl border border-border bg-card text-card-foreground shadow-2xl',
              size === 'sm' && 'max-w-md',
              size === 'md' && 'max-w-xl',
              size === 'lg' && 'max-w-3xl',
              size === 'xl' && 'max-w-5xl',
            )"
          >
            <header v-if="title || $slots.header" class="flex items-start justify-between gap-3 border-b border-border px-6 py-4">
              <div class="min-w-0">
                <h2 v-if="title" class="text-base font-semibold leading-6 text-foreground">{{ title }}</h2>
                <p v-if="description" class="mt-1 text-sm text-muted-foreground">{{ description }}</p>
                <slot name="header" />
              </div>
              <button
                v-if="!hideClose"
                type="button"
                class="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                @click="requestClose"
                aria-label="close"
              >
                <X class="h-4 w-4" />
              </button>
            </header>

            <div class="step-scrollbar flex-1 overflow-y-auto px-6 py-5">
              <slot />
            </div>

            <footer v-if="$slots.footer" class="flex items-center justify-end gap-2 border-t border-border bg-muted/30 px-6 py-3">
              <slot name="footer" />
            </footer>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>
